import React, { useState, useEffect } from "react";
import {
  Box, Stack, Select, MenuItem, Button, Typography,
} from "@mui/material";
import StyledInputField from "../../Components/Custom/StyledInputField";
import { align_column, VM_Colors } from "../../Styles/VMStyles";
import MuiTypographyMandatory from "../../Components/Custom/MuiTypographyMandatory";
import {
  API_ADD_ROOMS,
  API_UPDATE_ROOM,
} from "../../Util/URLConstants";
import axios from "axios";
import Loader from "../../Components/Loader";
import ConfirmationDialog from "../../Components/ConfirmationDialog";
import { roomTypeOptions } from "../../Util/ConstnatUtils";
import { isEmpty } from "../../Util/UtilHelper";
import { toast } from "react-toastify";

const ROOM_ALREADY_EXISTS_MESSAGE = "Room with same name and type already exists";

const isRoomAlreadyExistsError = (data) => {
  if (!data) return false;
  const message = String(data.message || "").toLowerCase();
  return (
    data.status_code === 400 &&
    (message.includes("already exists") ||
      message.includes("same name and type"))
  );
};

/**
 * @param {object|null} roomData - Existing room for edit, or template for duplicate
 * @param {boolean} isDuplicate - When true, prefill all fields except room number; always creates a new room
 */
const AddUpdateRooms = ({ userId, roomData = null, isDuplicate = false, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [existsDialog, setExistsDialog] = useState({
    open: false,
    message: ROOM_ALREADY_EXISTS_MESSAGE,
  });

  const [form, setForm] = useState({
    room_name: "",
    room_type: "Select",
    room_custom_type: "",
    no_of_persons: "",
    room_cost: "",
    guest_cost: "",
  });

  useEffect(() => {
    if (roomData) {
      const incomingType = (roomData.room_type || "").trim();
      // Case-insensitive match so "Single room" maps to "Single Room"
      const matchedType = roomTypeOptions.find(
        (opt) =>
          opt !== "Select" &&
          opt.toLowerCase() === incomingType.toLowerCase()
      );
      const isCustomCategory = !matchedType;

      setForm({
        // Duplicate: clear room number so user only enters a new one
        room_name: isDuplicate ? "" : roomData.room_name || "",
        room_type: isCustomCategory ? "Others" : matchedType || "Select",
        room_custom_type: isCustomCategory ? incomingType : "",
        no_of_persons: roomData.no_of_persons || "",
        room_cost: roomData.room_cost || "",
        guest_cost: roomData.guest_cost || "",
      });
    }
  }, [roomData, isDuplicate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const {  room_name, room_type, room_custom_type, room_cost, guest_cost } = form;

    if (!room_name || !room_type || !room_cost || room_type === "Select" || !guest_cost) {
      toast.error("All fields are required");
      return;
    }

    if (room_type === "Others" && isEmpty(room_custom_type)) {
      toast.error("Room Category is required for 'Others'");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...form,
        room_type: room_type === "Others" ? room_custom_type : room_type,
      };

      // Duplicate always creates a new room (ignore source room_id)
      let response;
      if (roomData?.room_id && !isDuplicate) {
        response = await axios.post(API_UPDATE_ROOM, {
          ...payload,
          room_id: roomData.room_id,
        });
      } else {
        response = await axios.post(API_ADD_ROOMS, {
          ...payload,
          user_id: userId,
        });
      }

      const respData = response?.data;
      if (isRoomAlreadyExistsError(respData)) {
        setExistsDialog({
          open: true,
          message: respData.message || ROOM_ALREADY_EXISTS_MESSAGE,
        });
        return;
      }

      if (respData?.status_code && respData.status_code !== 200) {
        toast.error(respData.message || "Failed to save/update room");
        return;
      }

      toast.success(
        isDuplicate ? "Room duplicated successfully!" : "Room saved successfully!"
      );
      onSuccess?.();
    } catch (error) {
      console.error("Room submission error:", error);
      const respData = error?.response?.data;
      if (isRoomAlreadyExistsError(respData)) {
        setExistsDialog({
          open: true,
          message: respData.message || ROOM_ALREADY_EXISTS_MESSAGE,
        });
        return;
      }
      toast.error(respData?.message || "Failed to save/update room");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, overflowY: "auto", px: 3, py: 3 }}>
      <Box sx={{ bgcolor: VM_Colors.white2, borderRadius: 2, p: 2 }}>
        {isDuplicate && (
          <Typography
            variant="body2"
            sx={{ mb: 2, color: "text.secondary", lineHeight: 1.5 }}
          >
            Details copied from the selected room. Enter a new room number and
            submit to create another room with the same settings.
          </Typography>
        )}
        <Stack spacing={3}>
          
          <Box sx={align_column}>
            <MuiTypographyMandatory label="Room Category" required />
            <Select
              name="room_type"
              value={form.room_type}
              onChange={handleChange}
              sx={{ backgroundColor: VM_Colors.white }}
              displayEmpty
            >
              <MenuItem value="" disabled>  -- Select --   </MenuItem>
              {roomTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
              <MenuItem value="Others">Others</MenuItem>
            </Select>
          </Box>

          {form.room_type === "Others" && (
            <StyledInputField
              heading="Custom Room Category"
              name="room_custom_type"
              required
              fullWidth
              value={form.room_custom_type}
              onChange={handleChange}
            />
          )}

          <StyledInputField
            heading="Room Number"
            name="room_name"
            required
            fullWidth
            value={form.room_name}
            onChange={handleChange}
          />

          <Box sx={align_column}>
            <MuiTypographyMandatory label="Room Capacity" required />
            <Select
              name="no_of_persons"
              value={form.no_of_persons}
              onChange={handleChange}
              sx={{ backgroundColor: VM_Colors.white }}
             displayEmpty
            >
              <MenuItem value="" disabled>  -- Select --   </MenuItem>
              {[...Array(10).keys()].slice(1).map((num) => (
                <MenuItem key={num} value={num}>{num}</MenuItem>
              ))}
            </Select>
          </Box>

          <StyledInputField
            heading="Room Cost"
            name="room_cost"
            required
            type="number"
            fullWidth
            value={form.room_cost}
            onChange={handleChange}
          />

          <StyledInputField
            heading="Additional Guest Cost"
            name="guest_cost"
            type="number"
            fullWidth
            required
            value={form.guest_cost}
            onChange={handleChange}
          />
        </Stack>

        <Box sx={{ textAlign: "right", mt: 3 }}>
          <Button onClick={handleSubmit} variant="contained" sx={{ px: 4 }}>
            Submit
          </Button>
        </Box>
      </Box>
      {loading && <Loader loading={loading} />}

      <ConfirmationDialog
        open={existsDialog.open}
        title="Room Already Exists"
        message={
          existsDialog.message ||
          "A room with the same name and type already exists. Please use a different room number."
        }
        okOnly
        onClose={() =>
          setExistsDialog({
            open: false,
            message: ROOM_ALREADY_EXISTS_MESSAGE,
          })
        }
      />
    </Box>
  );
};

export default AddUpdateRooms;
